'use client';

import {
  BookOpen,
  Command,
  FolderKanban,
  PuzzleIcon as PuzzlePiece,
  Tags,
  Users,
  ExternalLink,
  Home,
} from 'lucide-react';
import Link from 'next/link';
import * as React from 'react';

import { Route, ROUTES } from '@/constants/routes';
import { useConfigurations } from '@/hooks/useConfigurations';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/shared/shadcn/components/sidebar';
import { useAdminPermissions } from '@/components/admin/hooks/useAdminPermissions';
import { useRouter } from 'next/navigation';
import { NavConfiguration } from './NavConfiguration';
import { NavManagement } from './NavManagement';
import { NavSecondary } from './NavSecondary';
import { NavUser } from './NavUser';

const FIRST_ITEM_INDEX = 0;

interface ConfigItem {
  title: string;
  route: Route;
}

interface ConfigGroup {
  title: string;
  items: ConfigItem[];
}

const additionalNavConfiguration = [
  {
    title: 'Connectors',
    items: [
      {
        title: 'Register in MINERVA-NET',
        route: ROUTES.ADMIN.REGISTER_IN_MINERVA_NET,
      },
    ],
  },
];

export const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>): JSX.Element => {
  const router = useRouter();
  const { pageConfigs: getGeneratedConfigs } = useConfigurations({ configTypes: [] });
  const { canViewProjects, canViewUsers, canViewPlugins, canViewAnnotation, canViewConfig } =
    useAdminPermissions();
  const [adminPanelUrl, setAdminPanelUrl] = React.useState<string>('#');

  React.useEffect(() => {
    const url =
      (window as { config?: { ADMIN_PANEL_URL?: string } }).config?.ADMIN_PANEL_URL || '#';
    setAdminPanelUrl(url);
  }, []);

  // Build navigation items based on permissions
  const navManagementItems = [];

  if (canViewProjects) {
    navManagementItems.push({
      title: 'Projects',
      route: ROUTES.ADMIN.PROJECTS,
      icon: FolderKanban,
    });
  }

  if (canViewUsers) {
    navManagementItems.push({
      title: 'Users',
      route: ROUTES.ADMIN.USERS,
      icon: Users,
    });
  }

  if (canViewPlugins) {
    navManagementItems.push({
      title: 'Plugins',
      route: ROUTES.ADMIN.PLUGINS,
      icon: PuzzlePiece,
    });
  }

  if (canViewAnnotation) {
    navManagementItems.push({
      title: 'Annotation',
      route: ROUTES.ADMIN.ANNOTATORS.ROOT,
      icon: Tags,
    });
  }

  // Transform configuration structure for NavConfiguration component
  const transformedConfigs: {
    title: string;
    route: Route;
    items?: { title: string; route: Route }[];
  }[] = [];

  // Transform generated configs
  if (getGeneratedConfigs && Array.isArray(getGeneratedConfigs)) {
    getGeneratedConfigs.forEach((group: ConfigGroup) => {
      if (group.items && Array.isArray(group.items) && group.items.length > FIRST_ITEM_INDEX) {
        // Create a parent item that uses the first subitem's URL
        transformedConfigs.push({
          title: group.title,
          route: group.items[FIRST_ITEM_INDEX].route, // Use first item's URL as fallback
          items: group.items,
        });
      }
    });
  }

  // Add additional configs
  additionalNavConfiguration.forEach(group => {
    if (group.items && Array.isArray(group.items) && group.items.length > FIRST_ITEM_INDEX) {
      transformedConfigs.push({
        title: group.title,
        route: group.items[FIRST_ITEM_INDEX].route, // Use first item's URL as fallback
        items: group.items,
      });
    }
  });

  const data = {
    navManagement: navManagementItems,
    navConfiguration: transformedConfigs,
    navSecondary: [
      {
        title: 'Legacy Admin',
        url: adminPanelUrl,
        icon: ExternalLink,
        external: true,
      },
      {
        title: 'API Docs',
        url: 'https://minerva-dev.lcsb.uni.lu/minerva/docs/',
        icon: BookOpen,
      },
    ],
  };

  return (
    <Sidebar variant="inset" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href={ROUTES.ADMIN.PROJECTS.route} as={`${ROUTES.ADMIN.PROJECTS.url}`}>
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  <Command className="size-4" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">MINERVA</span>
                  <span className="truncate text-xs">Admin Panel</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link
                href={ROUTES.HOME.route}
                as={ROUTES.HOME.url}
                onClick={event => {
                  event.preventDefault();
                  router.push(ROUTES.HOME.url);
                  router.refresh();
                }}
              >
                {' '}
                <Home className="size-4" />
                <span>Go to the default map</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavManagement items={data.navManagement} />
        {canViewConfig && data.navConfiguration.length > FIRST_ITEM_INDEX && (
          <NavConfiguration items={data.navConfiguration} />
        )}
        <NavSecondary items={data.navSecondary} className="mt-auto" />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
};
